/* Local profile-photo framing. Only the confirmed square is handed to uploads. */
(() => {
  "use strict";
  let active = null;
  const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
  function frame(width, height, zoom, x, y) {
    const side = Math.min(width, height) / zoom;
    return { side, x: clamp(x, side / 2, width - side / 2), y: clamp(y, side / 2, height - side / 2) };
  }

  function open(source) {
    active?.();
    return new Promise(resolve => {
      const dialog = document.createElement("dialog");
      dialog.className = "sheet-dialog profile-photo-dialog";
      dialog.setAttribute("aria-labelledby", "profile-photo-title");
      dialog.innerHTML = `<div class="profile-photo-body">
        <h2 id="profile-photo-title">Adjust profile photo</h2>
        <p id="profile-photo-help">Drag your photo inside the circle. Use the slider to zoom, or arrow keys to move.</p>
        <div class="profile-photo-stage" tabindex="0" role="group" aria-label="Photo position" aria-describedby="profile-photo-help">
          <canvas width="640" height="640" aria-hidden="true"></canvas>
        </div>
        <label class="field"><span>Zoom</span><input class="profile-photo-zoom" type="range" min="1" max="4" step="0.01" value="1" disabled></label>
        <p class="profile-photo-status" role="status">Loading photo…</p>
        <div class="profile-photo-actions"><button type="button" class="secondary-button" data-photo-center disabled>Center</button>
        <button type="button" class="secondary-button" data-photo-cancel>Cancel</button>
        <button type="button" class="publish-button" data-photo-use disabled>Use photo</button></div>
      </div>`;
      const stage = dialog.querySelector(".profile-photo-stage");
      const canvas = dialog.querySelector("canvas");
      const zoom = dialog.querySelector("input");
      const status = dialog.querySelector(".profile-photo-status");
      const use = dialog.querySelector("[data-photo-use]");
      const center = dialog.querySelector("[data-photo-center]");
      let closed = false, image = null, objectUrl = null, position = null, busy = false;
      const pointers = new Map();
      let gesture = null;
      const abort = new AbortController();
      const finish = result => {
        if (closed) return;
        closed = true; abort.abort();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        dialog.close(); dialog.remove();
        if (active === cancel) active = null;
        resolve(result);
      };
      const cancel = () => finish(null);
      active = cancel;
      dialog.addEventListener("cancel", event => { event.preventDefault(); cancel(); });
      dialog.addEventListener("close", cancel);
      dialog.querySelector("[data-photo-cancel]").addEventListener("click", cancel);
      const draw = () => {
        if (!image || closed) return;
        position = frame(image.naturalWidth, image.naturalHeight, Number(zoom.value), position.x, position.y);
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, 640, 640);
        ctx.drawImage(image, position.x - position.side / 2, position.y - position.side / 2,
          position.side, position.side, 0, 0, 640, 640);
        zoom.setAttribute("aria-valuetext", `${Math.round(Number(zoom.value) * 100)} percent`);
      };
      zoom.addEventListener("input", draw);
      center.addEventListener("click", () => {
        zoom.value = "1";
        position = { x: image.naturalWidth / 2, y: image.naturalHeight / 2 };
        draw();
      });
      stage.addEventListener("keydown", event => {
        if (!image || busy) return;
        const delta = { ArrowLeft: [1, 0], ArrowRight: [-1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] }[event.key];
        if (!delta) return;
        event.preventDefault();
        const step = position.side * (event.shiftKey ? 0.1 : 0.02);
        position.x += delta[0] * step; position.y += delta[1] * step; draw();
      });
      function resetGesture() {
        const points = [...pointers.values()];
        if (!points.length) { gesture = null; return; }
        const midpoint = points.length === 1 ? points[0] : {
          x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2,
        };
        gesture = { ...midpoint, position: { ...position }, zoom: Number(zoom.value),
          distance: points.length > 1 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0 };
      }
      stage.addEventListener("pointerdown", event => {
        if (!image || busy || (event.pointerType === "mouse" && event.button !== 0)) return;
        event.preventDefault(); stage.focus(); stage.setPointerCapture(event.pointerId);
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY }); resetGesture();
      });
      stage.addEventListener("pointermove", event => {
        if (!pointers.has(event.pointerId) || !gesture || busy) return;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const points = [...pointers.values()];
        const midpoint = points.length === 1 ? points[0] : {
          x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2,
        };
        if (points.length > 1 && gesture.distance) {
          zoom.value = String(clamp(gesture.zoom * Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) / gesture.distance, 1, 4));
        }
        const scale = gesture.position.side / stage.getBoundingClientRect().width;
        position.x = gesture.position.x - (midpoint.x - gesture.x) * scale;
        position.y = gesture.position.y - (midpoint.y - gesture.y) * scale; draw();
      });
      for (const type of ["pointerup", "pointercancel", "lostpointercapture"]) {
        stage.addEventListener(type, event => { pointers.delete(event.pointerId); resetGesture(); });
      }
      use.addEventListener("click", async () => {
        if (!image || busy) return;
        busy = true; use.disabled = zoom.disabled = center.disabled = true;
        status.textContent = "Preparing photo…";
        try {
          const output = document.createElement("canvas");
          output.width = output.height = Math.min(768, Math.max(1, Math.round(position.side)));
          const ctx = output.getContext("2d");
          ctx.drawImage(image, position.x - position.side / 2, position.y - position.side / 2,
            position.side, position.side, 0, 0, output.width, output.height);
          const blob = await new Promise(resolveBlob => output.toBlob(resolveBlob, "image/png"));
          if (!blob) throw new Error("Could not prepare this photo. Try another image.");
          finish(new File([blob], "profile-photo.png", { type: "image/png" }));
        } catch (error) {
          if (closed) return;
          status.textContent = error.message; busy = false; use.disabled = zoom.disabled = center.disabled = false;
        }
      });
      document.body.append(dialog); dialog.showModal();
      (async () => {
        try {
          let blob = source;
          if (typeof source === "string") {
            const url = new URL(source, location.href);
            if (url.origin !== location.origin) throw new Error("Choose the original photo to adjust it.");
            const response = await fetch(url, { signal: abort.signal, credentials: "same-origin" });
            if (!response.ok) throw new Error("Could not load this photo. Choose it again from your device.");
            blob = await response.blob();
          }
          if (closed) return;
          if (!/^image\/(jpeg|png|webp|gif)$/.test(blob.type) || blob.size > 25 * 1024 * 1024) {
            throw new Error("Choose a JPG, PNG, WebP or GIF photo smaller than 25 MB.");
          }
          objectUrl = URL.createObjectURL(blob);
          const loaded = new Image(); loaded.src = objectUrl;
          await loaded.decode();
          if (closed) return;
          if (!loaded.naturalWidth || loaded.naturalWidth * loaded.naturalHeight > 40_000_000) {
            throw new Error("This photo is too large. Choose one below 40 megapixels.");
          }
          image = loaded;
          position = { x: image.naturalWidth / 2, y: image.naturalHeight / 2 };
          draw(); use.disabled = zoom.disabled = center.disabled = false;
          status.textContent = blob.type === "image/gif" ? "The profile photo will be saved as a still image." : "Your photo changes when you save your profile.";
          stage.focus();
        } catch (error) { if (!closed) status.textContent = error.message || "Could not read this photo. Try another image."; }
      })();
    });
  }
  window.LouderrProfilePhoto = { open, cancel: () => active?.() };
})();
